import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as Logo } from '../../svg/newlogo.svg';
import styles from '../../styles/centumnewintroducemobile.module.css';
import { useCentumBriefingStats } from '../../lib/useCentumBriefingStats';

/* ─────────────────────────────────────────────────────────────
   수능선배 김포점 선공개/미리보기 (/gimpo_newintroduce)
   원본: Claude Design "김포점 선공개 Mobile.html" (모바일 전용 375 프레임).
   동탄점 선공개와 디자인이 동일(콘텐츠만 차이)하여 동일한 CSS 모듈을 재사용한다.
   375 고정 → width:100% + max-width:500px 반응형으로 이식, 사진(위)→
   라벨+헤드라인+설명(아래) 구조를 섹션별로 그대로 재현한다.
   ───────────────────────────────────────────────────────────── */

const CDN = 'https://pub-81fe206b05a84c2da16285ce68449232.r2.dev';
const IMG = `${CDN}/sunbae/image`;
const APP = `${CDN}/sunbae/app-bundles`;
const LIFE = `${CDN}/sunbae/homepage/life`;
const FAC = `${CDN}/sunbae/homepage/facilities`;
const OPS = `${CDN}/sunbae/homepage/operations`;

// 선배 리포트 / 상담일지 외부 링크
const DAILY_REPORT_URL = 'https://suneungsunbae.com/dailyreportsample';
const REPORT_IMG = `${APP}/consultreport3.png`;

// ── 01 김포점 시설 캐러셀 (원본 8장 그대로) ──
const SLIDES = [
  { src: `${IMG}/gimpo1.jpg`, alt: '김포점 데스크', cap: '김포점 데스크' },
  { src: `${IMG}/gimpo2.jpg`, alt: '김포점 턴게이트', cap: '김포점 턴게이트' },
  { src: `${IMG}/gimpo3.jpg`, alt: '김포점 복도', cap: '김포점 복도' },
  { src: `${IMG}/gimpo4.jpg`, alt: '김포점 자습실', cap: '김포점 자습실' },
  { src: `${IMG}/gimpo5.jpg`, alt: '김포점 자습실 2', cap: '김포점 자습실 2' },
  { src: `${IMG}/gimpo6.jpg`, alt: '김포점 휴게실', cap: '김포점 휴게실' },
  { src: `${IMG}/gimpo7.jpg`, alt: '김포점 휴게실 2', cap: '김포점 휴게실 2' },
  { src: `${IMG}/gimpo8.jpg`, alt: '김포점 휴게실 3', cap: '김포점 휴게실 3' },
];

const INDEX_ROWS = [
  { n: '01', t: '김포점 시설', id: 'sec-01' },
  { n: '02', t: '생활관리', id: 'sec-02' },
  { n: '03', t: '학습관리', id: 'sec-03' },
  { n: '04', t: '운영관리', id: 'sec-04' },
  { n: '05', t: '공간관리', id: 'sec-05' },
];

const STATS = [
  { v: '98%', k: '상담 만족도' },
  { v: '15대1', k: '멘토 선발 경쟁률' },
  { v: '123명', k: '검증된 멘토진' },
  { v: '92%', k: '성적 향상 경험' },
];

const VALUE_PROPS = [
  { t: '- 엄격한 멘토 선발 기준', d: '학벌은 기본입니다.\n수능선배는 실제 수험 경험과 설명 능력을 갖춘\n상위권 멘토만을 선발합니다.' },
  { t: '- 표준화된 5단계 상담 프로세스', d: '멘토 개인의 역량에만 의존하지 않습니다.\n수능선배가 설계한 상담 기준에 따라\n일관된 상담 퀄리티를 유지합니다.' },
  { t: '- 데이터 기반의 정교한 매칭', d: '단순한 학벌 매칭이 아닙니다.\n수강 강사, 취약 과목, 학습 성향을 분석해\n가장 가까운 성공 경험을 가진 멘토를 연결합니다.' },
  { t: '- 지속적인 퀄리티 모니터링', d: '매월 11개 항목의 학생 평가와 본사 검토를 통해\n멘토링 퀄리티를 지속적으로 점검합니다.' },
];

const FEATURES = [
  { i: '01', name: '출석 & 벌점', desc: '모든 입퇴실 기록 일자별로 확인' },
  { i: '02', name: '정기일정', desc: '매월 1일 외부 일정 등록' },
  { i: '03', name: '상담일지', desc: '담임멘토와의 모든 상담 기록 누적 조회' },
  { i: '04', name: 'Off 질의응답', desc: '대면 질의응답 스케줄 신청' },
  { i: '05', name: 'On 질의응답', desc: '온라인 익명 게시판 질문' },
  { i: '06', name: '도시락 신청', desc: '충전식 결제로 중식 / 석식 신청' },
  { i: '07', name: '의견보내기', desc: '익명으로 불편 사항 접수' },
  { i: '08', name: '영단어 시험', desc: '일일 영단어 암기 및 시험응시' },
  { i: '09', name: '사유 제출', desc: '결석/지각/조퇴/외출 사유 즉시 제출' },
  { i: '10', name: '메시지', desc: '학원관리자·학부모와 실시간 소통' },
  { i: '11', name: '방화벽 해제', desc: '학습 목적 원내 wifi 방화벽 해제' },
];

// reveal 요소에 붙일 공통 props (className + data-reveal 마커).
// data-* 속성을 스프레드로 넘기면 TS 가 막을 수 있어 any 로 반환한다.
const reveal = (cls?: string): any => ({
  className: cls ? `${cls} ${styles.reveal}` : styles.reveal,
  'data-reveal': true,
});

// cell(배경 이미지) 헬퍼 — .cell 클래스의 center/cover/no-repeat 위에 이미지만 얹는다
const bg = (url: string): React.CSSProperties => ({ backgroundImage: `url('${url}')` });

const PrevArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 6 9 12 15 18" /></svg>
);
const NextArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
);

// 0 → target 카운트업 (cubic-ease-out, 1.4s — centum_seatlist 와 동일)
function useCountUp(target: number, duration: number, start: boolean) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) { setVal(0); return; }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

export default function GimpoNewIntroduceMobile() {
  const rootRef = useRef<HTMLDivElement>(null);

  // 누적 설명회 신청자 (실데이터) — 김포점 설명회 페이지와 동일한 API 훅(location=gimpo) 사용
  const stats = useCentumBriefingStats('gimpo');
  const hasTotal = typeof stats.total === 'number';
  const liveCount = useCountUp(stats.total ?? 0, 1400, hasTotal && !stats.loading);
  const liveSub =
    typeof stats.last24h === 'number' && stats.last24h > 0
      ? <>오늘만 <b>+{stats.last24h}명</b>이 신청했어요.</>
      : <>실시간으로 신청이 이어지고 있어요.</>;

  // ── 부드러운 섹션 이동 (sticky 헤더 높이는 scroll-margin-top 으로 처리) ──
  const scrollToSec = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── 스크롤 진입 시 페이드인 (reveal) ──
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add(styles.in));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.in);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // ── 01 시설 캐러셀 — 자동 넘김(3s) + 화살표 + 세그먼트 바 + 스와이프 + 화면 밖 정지 ──
  const n = SLIDES.length;
  const [cur, setCur] = useState(0);
  const cvRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const visibleRef = useRef(true);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const schedule = useCallback(() => {
    clearTimer();
    if (pausedRef.current || !visibleRef.current) return;
    timerRef.current = window.setInterval(() => setCur((c) => (c + 1) % n), 3000);
  }, [clearTimer, n]);
  const goTo = useCallback(
    (i: number) => {
      setCur(((i % n) + n) % n);
      schedule();
    },
    [n, schedule]
  );

  useEffect(() => {
    schedule();
    const cv = cvRef.current;
    let io: IntersectionObserver | null = null;
    if (cv && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            visibleRef.current = e.isIntersecting;
            schedule();
          });
        },
        { threshold: 0.4 }
      );
      io.observe(cv);
    }
    return () => {
      clearTimer();
      io?.disconnect();
    };
  }, [schedule, clearTimer]);

  const swipe = useRef({ sx: 0, dx: 0, active: false });
  const onTouchStart = (e: React.TouchEvent) => {
    swipe.current = { sx: e.touches[0].clientX, dx: 0, active: true };
    pausedRef.current = true;
    clearTimer();
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (swipe.current.active) swipe.current.dx = e.touches[0].clientX - swipe.current.sx;
  };
  const onTouchEnd = () => {
    if (!swipe.current.active) return;
    swipe.current.active = false;
    const dx = swipe.current.dx;
    pausedRef.current = false;
    if (Math.abs(dx) > 40) goTo(cur + (dx < 0 ? 1 : -1));
    else schedule();
  };

  // ── 상담일지 라이트박스 — 줌/패닝/핀치 (centum_seatlist 패턴 이식) ──
  const lbRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const lbImgRef = useRef<HTMLImageElement>(null);
  const pctRef = useRef<HTMLButtonElement>(null);
  const reportBtnRef = useRef<HTMLButtonElement>(null);
  const inRef = useRef<HTMLButtonElement>(null);
  const outRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const lb = lbRef.current;
    const stage = stageRef.current;
    const img = lbImgRef.current;
    const pct = pctRef.current;
    const reportBtn = reportBtnRef.current;
    const closeBtn = closeRef.current;
    const inBtn = inRef.current;
    const outBtn = outRef.current;
    if (!lb || !stage || !img || !pct) return;

    const MIN = 1, MAX = 6;
    let scale = 1, tx = 0, ty = 0;
    let baseW = 0, baseH = 0;

    function measureBase() {
      img!.style.maxWidth = '92vw';
      img!.style.maxHeight = '88vh';
      img!.style.width = 'auto';
      img!.style.height = 'auto';
      img!.style.transform = 'none';
      baseW = img!.clientWidth;
      baseH = img!.clientHeight;
      img!.style.maxWidth = 'none';
      img!.style.maxHeight = 'none';
    }
    function apply() {
      if (baseW) {
        img!.style.width = baseW * scale + 'px';
        img!.style.height = baseH * scale + 'px';
      }
      img!.style.transform = `translate(${tx}px, ${ty}px)`;
      pct!.textContent = Math.round(scale * 100) + '%';
      stage!.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    }
    function clampPan() {
      const r = img!.getBoundingClientRect();
      const maxX = Math.max(0, (r.width - stage!.clientWidth) / 2 + 40);
      const maxY = Math.max(0, (r.height - stage!.clientHeight) / 2 + 40);
      tx = Math.min(maxX, Math.max(-maxX, tx));
      ty = Math.min(maxY, Math.max(-maxY, ty));
    }
    function setScale(nextRaw: number, cx: number, cy: number) {
      const nextV = Math.min(MAX, Math.max(MIN, nextRaw));
      if (nextV === scale) return;
      const rect = stage!.getBoundingClientRect();
      const ox = cx - rect.left - rect.width / 2 - tx;
      const oy = cy - rect.top - rect.height / 2 - ty;
      const k = nextV / scale;
      tx -= ox * (k - 1);
      ty -= oy * (k - 1);
      scale = nextV;
      if (scale === 1) { tx = 0; ty = 0; }
      clampPan();
      apply();
    }
    function reset() { scale = 1; tx = 0; ty = 0; apply(); }
    function open() {
      lb!.classList.add(styles.pvLbOpen);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => { measureBase(); reset(); });
    }
    function close() {
      lb!.classList.remove(styles.pvLbOpen);
      document.body.style.overflow = '';
    }

    const onReport = () => open();
    reportBtn?.addEventListener('click', onReport);
    const onClose = () => close();
    closeBtn?.addEventListener('click', onClose);
    const onIn = () => { const r = stage!.getBoundingClientRect(); setScale(scale + 0.5, r.left + r.width / 2, r.top + r.height / 2); };
    const onOut = () => { const r = stage!.getBoundingClientRect(); setScale(scale - 0.5, r.left + r.width / 2, r.top + r.height / 2); };
    inBtn?.addEventListener('click', onIn);
    outBtn?.addEventListener('click', onOut);
    const onPct = () => reset();
    pct.addEventListener('click', onPct);

    const onStageClick = (e: MouseEvent) => { if (e.target === stage && scale === 1) close(); };
    stage.addEventListener('click', onStageClick);
    const onDbl = (e: MouseEvent) => { e.preventDefault(); setScale(scale > 1 ? 1 : 2.4, e.clientX, e.clientY); };
    img.addEventListener('dblclick', onDbl);
    const onKey = (e: KeyboardEvent) => {
      if (!lb!.classList.contains(styles.pvLbOpen)) return;
      if (e.key === 'Escape') close();
      else if (e.key === '+' || e.key === '=') { const r = stage!.getBoundingClientRect(); setScale(scale + 0.5, r.left + r.width / 2, r.top + r.height / 2); }
      else if (e.key === '-') { const r = stage!.getBoundingClientRect(); setScale(scale - 0.5, r.left + r.width / 2, r.top + r.height / 2); }
    };
    document.addEventListener('keydown', onKey);
    const onWheel = (e: WheelEvent) => { e.preventDefault(); setScale(scale * (e.deltaY < 0 ? 1.12 : 0.89), e.clientX, e.clientY); };
    stage.addEventListener('wheel', onWheel, { passive: false });

    // 마우스 드래그 패닝
    let dragging = false, sx = 0, sy = 0;
    const onMouseDown = (e: MouseEvent) => {
      if (scale <= 1) return;
      dragging = true; sx = e.clientX - tx; sy = e.clientY - ty;
      stage!.classList.add(styles.pvLbStagePanning);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      tx = e.clientX - sx; ty = e.clientY - sy; clampPan(); apply();
    };
    const onMouseUp = () => { dragging = false; stage!.classList.remove(styles.pvLbStagePanning); };
    stage.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // 터치: 드래그(1) + 핀치(2) + 더블탭
    let pTx = 0, pTy = 0, pDist = 0, pScale = 1, pCx = 0, pCy = 0, lastTap = 0;
    const distOf = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTS = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        pTx = t.clientX - tx; pTy = t.clientY - ty;
        const now = Date.now();
        if (now - lastTap < 300) setScale(scale > 1 ? 1 : 2.4, t.clientX, t.clientY);
        lastTap = now;
      } else if (e.touches.length === 2) {
        pDist = distOf(e.touches); pScale = scale;
        pCx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        pCy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      }
    };
    const onTM = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1 && scale > 1) {
        const t = e.touches[0];
        tx = t.clientX - pTx; ty = t.clientY - pTy; clampPan(); apply();
      } else if (e.touches.length === 2) {
        const d = distOf(e.touches);
        if (pDist > 0) setScale(pScale * (d / pDist), pCx, pCy);
      }
    };
    stage.addEventListener('touchstart', onTS, { passive: false });
    stage.addEventListener('touchmove', onTM, { passive: false });

    return () => {
      reportBtn?.removeEventListener('click', onReport);
      closeBtn?.removeEventListener('click', onClose);
      inBtn?.removeEventListener('click', onIn);
      outBtn?.removeEventListener('click', onOut);
      pct.removeEventListener('click', onPct);
      stage.removeEventListener('click', onStageClick);
      img.removeEventListener('dblclick', onDbl);
      document.removeEventListener('keydown', onKey);
      stage.removeEventListener('wheel', onWheel);
      stage.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      stage.removeEventListener('touchstart', onTS);
      stage.removeEventListener('touchmove', onTM);
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className={styles.page} ref={rootRef}>
      <main className={styles.frame}>

        {/* ── Header (logo only) ── */}
        <header className={styles.pvHead}>
          <Logo />
        </header>

        {/* ── Hero ── */}
        <section>
          <div className={styles.pvHero}>
            <div className={styles.pvHeroEyebrow}>GIMPO · 미리 둘러보기</div>
            <h1 className={styles.pvHeroTitle}>{'수능선배 김포점\n'}<b>미리 둘러보기</b></h1>
            <p className={styles.pvHeroDesc}>{'초호화 멘토진의 1:1 담임 시스템과\n최고의 관리 시스템을 미리 둘러보세요.'}</p>
          </div>
          <div {...reveal(styles.pvLive)}>
            <div className={styles.lcLive}>
              <div className={styles.lcLiveTop}><span className={styles.lcLivePulse} />LIVE · 누적 설명회 신청자</div>
              <div className={styles.lcLiveCount}>
                <span className={styles.lcLiveNum}>{liveCount.toLocaleString()}</span>
                <span className={styles.lcLiveUnit}>명 신청</span>
              </div>
              <p className={styles.lcLiveSub}>{liveSub}</p>
            </div>
          </div>
          <div {...reveal(styles.pvIndex)}>
            {INDEX_ROWS.map((r) => (
              <a className={styles.pvIndexRow} href={`#${r.id}`} key={r.id} onClick={scrollToSec(r.id)}>
                <span className={styles.pvIndexN}>{r.n}</span>
                <span className={styles.pvIndexT}>{r.t}</span>
              </a>
            ))}
          </div>
        </section>

        {/* ── 01 김포점 시설 ── */}
        <section className={styles.pvSec} id="sec-01">
          <div {...reveal(styles.pvSecKicker)}>
            <span className={styles.pvSecNum}>01</span>
            <span className={styles.pvSecName}>김포점 시설</span>
            <span className={styles.pvSecLine} />
          </div>
          <div {...reveal(styles.pvCopy)} style={{ paddingBottom: 24 }}>
            <h2 className={styles.pvCopyTitle}>{'프리미엄 학습공간을\n미리 만나보세요'}</h2>
            <p className={styles.pvCopyDesc}>{'자습실부터 휴게공간, 상담실까지 — 김포점의 공간을 미리\n둘러보세요.'}</p>
          </div>

          <div
            {...reveal(styles.cv)}
            ref={cvRef}
            aria-label="김포점 시설 사진"
            onPointerEnter={() => { pausedRef.current = true; clearTimer(); }}
            onPointerLeave={() => { pausedRef.current = false; schedule(); }}
          >
            <div
              className={styles.cvViewport}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className={styles.cvTrack} style={{ transform: `translateX(${-cur * 100}%)` }}>
                {SLIDES.map((s, i) => (
                  <div className={styles.cvSlide} key={i}>
                    <img src={s.src} alt={s.alt} loading={i === 0 ? undefined : 'lazy'} />
                  </div>
                ))}
              </div>
              <button className={`${styles.cvNav} ${styles.cvNavPrev}`} type="button" aria-label="이전 사진" onClick={() => goTo(cur - 1)}><PrevArrow /></button>
              <button className={`${styles.cvNav} ${styles.cvNavNext}`} type="button" aria-label="다음 사진" onClick={() => goTo(cur + 1)}><NextArrow /></button>
            </div>
            <div className={styles.cvMeta}><span className={styles.cvCap}>{SLIDES[cur].cap}</span></div>
            <div className={styles.cvBar}>
              {SLIDES.map((_, i) => (
                <button
                  className={`${styles.cvSeg} ${i === cur ? styles.cvSegActive : ''}`}
                  type="button"
                  key={i}
                  aria-label={`${i + 1}번 사진`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── 02 생활관리 ── */}
        <section className={styles.pvSec} id="sec-02">
          <div {...reveal(styles.pvSecKicker)}>
            <span className={styles.pvSecNum}>02</span>
            <span className={styles.pvSecName}>생활관리</span>
            <span className={styles.pvSecLine} />
          </div>

          <div {...reveal(styles.pvSub)} style={{ marginTop: 8 }}>
            <h3 className={styles.pvSubTitle}>{'매 교시\n엄격한 학습관리'}</h3>
            <div className={styles.pvSubHead}>교시 중 졸음을 엄격히 제한</div>
            <div className={styles.pvSubRule} />
            <p className={styles.pvSubBody}>졸고 있는 학생을 깨워 휴게실 스탠딩 테이블로 이동해 환기하도록 권고합니다. 졸음 시간 및 횟수가 사감 패드에 기록되어, 교시 중 2회 연속 졸음 체크 시 벌점이 부여되고 학부모 알림톡이 전송됩니다.</p>
          </div>
          <div {...reveal(styles.pvSubimg)}>
            <img src={`${LIFE}/img8.webp`} alt="" />
          </div>
          <div {...reveal(styles.pvSubimg)} style={{ marginTop: 12 }}>
            <img src={`${LIFE}/img9.webp`} alt="" />
            <div className={styles.pvSubimgCap}>본사 순찰 가이드라인</div>
          </div>

          <div {...reveal(styles.pvSub)} style={{ marginTop: 32 }}>
            <div className={styles.pvSubHead} style={{ marginTop: 0 }}>{'전자기기를 학습 외 목적으로\n사용하지 않도록 감독'}</div>
            <div className={styles.pvSubRule} />
            <p className={styles.pvSubBody}>학생의 모든 출입기록은 수기가 아닌 얼굴인식 턴게이트를 통해 자동으로 서버에 저장되며 학부모님이 바로 확인이 가능하도록 카카오톡 또는 메세지로 발송됩니다.</p>
          </div>
          <div {...reveal(styles.pvSubimg)}>
            <img src={`${LIFE}/img10.webp`} alt="" />
          </div>

          <div {...reveal(styles.pvSub)} style={{ marginTop: 32 }}>
            <h3 className={styles.pvSubTitle}>{'모든 기록은 저장,\n그리고 공유'}</h3>
            <div className={styles.pvSubHead}>{'스마트 패드 및 QR 인증\n순찰기록은 매일 밤 선배 리포트로 발송'}</div>
            <div className={styles.pvSubRule} />
            <p className={styles.pvSubBody}>사감은 20분 간격으로 자습실 각 구역에 설치된 QR에 패드를 태깅하며 순찰합니다. 구역별 실제 순찰 시간이 스마트 패드를 통해 기록되며, 학생별로 일일 32회 순찰 결과가 '공부중, 졸음, 좌석에 없음'으로 표시되어 선배리포트로 발송됩니다.</p>
          </div>
          <div {...reveal(styles.pvSubimg)}>
            <img src={`${LIFE}/img11.webp`} alt="" />
          </div>
          <a {...reveal(styles.pvLinkbtn)} href={DAILY_REPORT_URL} target="_blank" rel="noopener noreferrer">
            <span className={styles.pvLinkbtnT}>선배 리포트 확인하기</span>
            <span className={styles.pvLinkbtnArrow} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"><line x1="4" y1="12" x2="20" y2="12" /><polyline points="14 6 20 12 14 18" /></svg>
            </span>
          </a>
        </section>

        {/* ── 03 학습관리 ── */}
        <section className={styles.pvSec} id="sec-03">
          <div {...reveal(styles.pvSecKicker)}>
            <span className={styles.pvSecNum}>03</span>
            <span className={styles.pvSecName}>학습관리</span>
            <span className={styles.pvSecLine} />
          </div>
          <div {...reveal(styles.pvCopy)}>
            <h2 className={styles.pvCopyTitle}>{'성공 노하우가 담긴\n초호화 담임 멘토 시스템'}</h2>
            <p className={styles.pvCopyDesc}>전국 수능 상위권 멘토가 매주 규칙적인 대면상담을 통해 개별 학습 계획을 세우고, 학습 흐름을 함께 점검합니다.</p>
          </div>
          <div {...reveal(styles.pvStats)}>
            {STATS.map((s) => (
              <div className={styles.pvStat} key={s.k}>
                <div className={styles.pvStatV}>{s.v}</div>
                <div className={styles.pvStatK}>{s.k}</div>
              </div>
            ))}
          </div>
          <div {...reveal(styles.pvReport)} style={{ marginTop: 40 }}>
            <div className={styles.pvReportLabel}>담임멘토 상담일지 예시</div>
            <button className={styles.pvReportBtn} type="button" ref={reportBtnRef} aria-label="담임멘토 상담일지 확대해서 보기">
              <img src={REPORT_IMG} alt="담임멘토 상담일지" loading="lazy" />
              <span className={styles.pvReportHint}><span className={styles.pvReportIco}>⤢</span> 확대</span>
            </button>
          </div>
          <div {...reveal(styles.pvVp)}>
            {VALUE_PROPS.map((vp) => (
              <div className={styles.pvVpRow} key={vp.t}>
                <div className={styles.pvVpT}>{vp.t}</div>
                <p className={styles.pvVpD}>{vp.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── 04 운영관리 ── */}
        <section className={styles.pvSec} id="sec-04">
          <div {...reveal(styles.pvSecKicker)}>
            <span className={styles.pvSecNum}>04</span>
            <span className={styles.pvSecName}>운영관리</span>
            <span className={styles.pvSecLine} />
          </div>
          <div {...reveal(styles.pvCopy)}>
            <h2 className={styles.pvCopyTitle}>{'학원의 모든 기능을\n하나의 앱으로'}</h2>
            <p className={styles.pvCopyDesc}>학생들의 효율적인 학원이용과 효과적인 학습관리를 위해 최적화된 운영체제와 프로그램을 자체적으로 개발하고 있습니다.</p>
          </div>
          <div {...reveal(`${styles.pvPhoto} ${styles.pvPhotoPanel}`)} style={{ marginTop: 40 }}>
            <img src={`${OPS}/8.webp`} alt="수능선배 전용 앱 메인 화면" />
          </div>
          <div {...reveal(styles.pvFeat)}>
            {FEATURES.map((f) => (
              <div className={styles.pvFeatRow} key={f.i}>
                <span className={styles.pvFeatIdx}>{f.i}</span>
                <div className={styles.pvFeatBody}>
                  <span className={styles.pvFeatName}>{f.name}</span>
                  <span className={styles.pvFeatDesc}>{f.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 05 공간관리 ── */}
        <section className={styles.pvSec} id="sec-05">
          <div {...reveal(styles.pvSecKicker)}>
            <span className={styles.pvSecNum}>05</span>
            <span className={styles.pvSecName}>공간관리</span>
            <span className={styles.pvSecLine} />
          </div>

          <div className={styles.mbGapTight} style={{ height: 8 }} />

          {/* 24시간 CO2 800ppm 이하 */}
          <div className={styles.spBlock}>
            <h3 {...reveal(styles.spBlockTitle)}>{'24시간 CO2 800ppm 이하,\n3배 용량의 환기 설비'}</h3>
            <div {...reveal(styles.spImgWide)} style={bg(`${FAC}/11_ventilation_ceiling_wide.webp`)} />
            <div {...reveal(styles.spImgPair)} style={{ gridTemplateColumns: '2fr 1fr' }}>
              <div className={styles.cell} style={{ aspectRatio: 'auto', height: 280, ...bg(`${FAC}/12_ventilation_lights.webp`) }} />
              <div className={styles.cell} style={{ aspectRatio: 'auto', height: 280, ...bg(`${FAC}/13_ventilation_diffuser.webp`) }} />
            </div>
            <div {...reveal(styles.spSub)}>
              <div className={styles.spSubT}>3배 용량의 최고급 청정환기 시스템 전열교환기</div>
              <p className={styles.spSubD}>최고급 환기 설비 장치가 전 구역에 평수 대비 적정 용량의 3배 이상으로 구축되어 있으며 전열교환기는 외부 오염 물질과 초미세먼지를 99.9% 차단하는 헤파(HEPA) 필터를 통해, 창문을 열지 않아도 24시간 내내 깨끗하고 신선한 공기를 실내에 공급합니다.</p>
            </div>
          </div>

          <div className={styles.mbGapTight} />

          {/* 자동 창문 개폐기 */}
          <div className={styles.spBlock}>
            <h3 {...reveal(styles.spBlockTitle)}>{'자동 창문 개폐기,\n365일 빠짐없는 환기'}</h3>
            <div {...reveal(styles.spImgWide)} style={{ overflow: 'hidden' }}>
              <video
                src={`${FAC}/widopen.mp4`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div {...reveal(styles.spImgPair)}>
              <div className={styles.cell} style={bg(`${FAC}/14_window_opener_1.webp`)} />
              <div className={styles.cell} style={bg(`${FAC}/15_window_opener_2.webp`)} />
            </div>
            <div {...reveal(styles.spSub)}>
              <div className={styles.spSubT}>{'하루도 빠짐없이 신선한 공기,\n자동 창문 개폐 시스템'}</div>
              <p className={styles.spSubD}>식사 시간(점심/저녁)에 맞춰 30~60분간 자동으로 창문을 개폐하여 대량의 자연 환기를 진행합니다. 자동 개폐 장치를 통해 학습 시간에는 소음을 완벽히 차단하고, 휴식 시간에는 공기의 질을 획기적으로 개선하여 학습 흐름을 방해하지 않습니다.</p>
            </div>
          </div>

          <div className={styles.mbGapTight} />

          {/* 최적의 온습도 유지 */}
          <div className={styles.spBlock}>
            <h3 {...reveal(styles.spBlockTitle)}>{'최적의 온습도 유지,\n10명 단위 구역별\n온습도 모니터링'}</h3>
            <div {...reveal(styles.spImgPair)} style={{ gridTemplateColumns: '2fr 1fr' }}>
              <div className={styles.cell} style={{ aspectRatio: 'auto', height: 280, ...bg(`${FAC}/16_ac_unit.webp`) }} />
              <div className={styles.cell} style={{ aspectRatio: 'auto', height: 280, ...bg(`${FAC}/17_thermostat.webp`) }} />
            </div>
            <div
              {...reveal(styles.spImgWide)}
              style={{
                aspectRatio: '758 / 505',
                backgroundColor: '#fff',
                backgroundPosition: 'center',
                backgroundSize: 'contain',
                ...bg(`${FAC}/18_temp_humidity_monitor.webp`),
              }}
            />
            <div {...reveal(styles.spSub)}>
              <div className={styles.spSubT}>쾌적한 공기 질을 위한 온습도 상시 모니터링</div>
              <p className={styles.spSubD}>원내에 구비된 온습도계를 상시 체크하며, 최적의 학습 환경을 위해 습도 40~50%, 온도 20~23도를 유지하고 있습니다.</p>
            </div>
          </div>
        </section>

        {/* ── Closing ── */}
        <footer className={styles.pvFoot}>
          <div {...reveal(styles.pvFootTitle)}>{'설명회 전,\n김포점을 미리 둘러보세요.'}</div>
          <div {...reveal(styles.pvFootMeta)}>수능선배 김포점 미리보기<br />© SNSB Education</div>
        </footer>

      </main>

      {/* ── 상담일지 라이트박스 ── */}
      <div className={styles.pvLb} ref={lbRef} role="dialog" aria-modal="true" aria-label="담임멘토 상담일지">
        <button className={styles.pvLbClose} type="button" ref={closeRef} aria-label="닫기">×</button>
        <div className={styles.pvLbStage} ref={stageRef}>
          <img className={styles.pvLbImg} ref={lbImgRef} src={REPORT_IMG} alt="담임멘토 상담일지" />
        </div>
        <div className={styles.pvLbZoom}>
          <button type="button" ref={outRef} aria-label="축소">−</button>
          <button type="button" className={styles.pvLbPct} ref={pctRef} aria-label="원래 크기">100%</button>
          <button type="button" ref={inRef} aria-label="확대">+</button>
        </div>
      </div>
    </div>
  );
}
